<!-- Секция FAQ - Organism -->
<template>
  <section class="bg-black relative overflow-hidden md:min-h-[880px] py-16">
    <!-- Фоновые элементы -->
    <div class="absolute inset-0">
      <div class="absolute inset-0 bg-[rgba(22,22,22,0.33)]" />
    </div>

    <div class="relative z-10 mx-auto max-w-7xl px-6">
      <!-- Заголовок -->
      <h2 class="faq-title text-center mb-6 md:mb-[20px]">
        FAQ
      </h2>

      <!-- Блок вопросов -->
      <div class="max-w-5xl mx-auto border border-white/15 rounded-2xl md:rounded-[25px] overflow-hidden">
        <div v-for="(item, index) in faqItems" :key="index" class="border-b border-white/15 last:border-b-0">
          <div class="w-full bg-black text-left py-6 md:py-[28px] hover:bg-white/5 transition-colors"
            style="padding-bottom: 12px; padding-top: 12px;" :class="{ 'bg-white/5': item.isOpen }">
            <div class="flex items-center justify-between gap-3 px-6 md:px-[36px] cursor-pointer"
              @click="toggleItem(index)">
              <BaseHeading :level="3" class="text-base md:text-xl font-normal text-white flex-1 min-w-0">
                {{ item.question }}
              </BaseHeading>

              <div class="shrink-0 transform transition-transform duration-200 w-5 md:w-auto"
                :class="{ '-rotate-90': item.isOpen }">
                <svg class="w-full h-auto" width="23" height="21" viewBox="0 0 23 21" fill="none"
                  xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path
                    d="M10.1959 1.6993L17.4912 8.99461L-0.000221369 8.99461L-0.000221791 11.3975L17.4912 11.3975L10.1959 18.6928L11.8952 20.3921L22.0913 10.196L11.8952 -4.55435e-05L10.1959 1.6993Z"
                    fill="white" />
                </svg>
              </div>
            </div>
          </div>

          <div class="faq-answer-wrapper overflow-hidden transition-all duration-300 ease-in-out"
            :style="{ maxHeight: item.isOpen ? `${item.contentHeight}px` : '0px' }">
            <div :ref="el => setAnswerRef(el, index)" class="bg-[#121212] px-6 py-6 md:px-[36px] md:py-[36px]">
              <BaseText class="text-sm md:text-base text-white leading-relaxed font-light" v-html="item.answer" />
            </div>
          </div>
        </div>
      </div>

      <!-- Блок с кнопкой поддержки -->
      <div class="max-w-5xl mx-auto mt-8 text-center">
        <BaseButton tone="white" :show-arrow="true" @click="handleSupportClick" class="font-light">
          Задать вопрос в поддержку
        </BaseButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import BaseHeading from '@/components/atoms/BaseHeading.vue'
import BaseText from '@/components/atoms/BaseText.vue'
import BaseButton from '@/components/atoms/BaseButton.vue'

interface FAQItem {
  question: string
  answer: string
  isOpen: boolean
  contentHeight?: number
}

const emit = defineEmits<{
  supportClick: []
}>()

const faqItems = ref<FAQItem[]>([
  {
    question: 'Как быстро загружаются треки на площадки?',
    answer: 'Треки загружаются на площадки в течение 24-48 часов после модерации. Время может варьироваться в зависимости от конкретной платформы.',
    isOpen: false,
    contentHeight: 0
  },
  {
    question: 'Какие площадки поддерживаются?',
    answer: 'Мы поддерживаем все основные музыкальные платформы: Spotify, Apple Music, YouTube Music, Amazon Music, Deezer, TikTok, VK Music и многие другие.',
    isOpen: false,
    contentHeight: 0
  },
  {
    question: 'Как работает система роялти?',
    answer: 'Вы получаете 95% от всех доходов с площадок. Выплаты производятся ежемесячно после достижения минимального порога в 1000 рублей.',
    isOpen: false,
    contentHeight: 0
  },
  {
    question: 'Можно ли подключить приложение без Telegram?',
    answer: `Сейчас все работает через Telegram-бота — это быстрый и удобный инструмент для управления релизами. Но мы не останавливаемся.<br><br>
    Уже в разработке — наше собственное приложение для <strong>App Store</strong> и <strong>Google Play</strong>. Оно объединит все функции в одном месте: загрузка треков, аналитика, отчеты по роялти и продвижение. Это значит, что очень скоро вы сможете управлять карьерой артиста с телефона,<br>
    не переключаясь между сервисами. Подписка сейчас — это доступ к будущему, где вся экосистема музыки будет у вас в кармане.`,
    isOpen: false,
    contentHeight: 0
  },
  {
    question: 'Что делать, если нужна помощь по продвижению?',
    answer: 'У нас есть команда маркетинговых специалистов, которые помогут с продвижением вашей музыки. Обратитесь к личному менеджеру для консультации.',
    isOpen: false,
    contentHeight: 0
  },
  {
    question: 'Как артисту перейти на тариф лейбла?',
    answer: 'Для перехода на тариф лейбла обратитесь к вашему менеджеру или в службу поддержки. Мы поможем настроить аккаунт лейбла и перенести всех артистов.',
    isOpen: false,
    contentHeight: 0
  }
])

// Храним ссылки на DOM-элементы ответов
const answerRefs = ref<(HTMLElement | null)[]>([])

const setAnswerRef = (el: any, index: number) => {
  if (el) {
    answerRefs.value[index] = el as HTMLElement
  }
}

const toggleItem = (index: number) => {
  faqItems.value[index].isOpen = !faqItems.value[index].isOpen
}

const handleSupportClick = () => {
  emit('supportClick')
}

// Функция для пересчета высоты контента
const recalculateHeights = () => {
  answerRefs.value.forEach((el, index) => {
    if (el && faqItems.value[index]) {
      // scrollHeight уже включает padding элемента
      faqItems.value[index].contentHeight = el.scrollHeight
    }
  })
}

// Debounce функция для оптимизации resize
let resizeTimeout: ReturnType<typeof setTimeout> | null = null
const debouncedRecalculate = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
  }
  resizeTimeout = setTimeout(() => {
    recalculateHeights()
  }, 100)
}

// Вычисляем высоту контента после монтирования
onMounted(async () => {
  await nextTick()
  // Небольшая задержка для полного рендеринга
  setTimeout(() => {
    recalculateHeights()
  }, 100)

  // Пересчитываем высоту при изменении размера окна
  window.addEventListener('resize', debouncedRecalculate)
})

// Убираем слушатель при размонтировании
onUnmounted(() => {
  window.removeEventListener('resize', debouncedRecalculate)
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
  }
})
</script>

<style scoped>
.faq-title {
  font-family: 'Halvar Breitschrift', sans-serif;
  font-size: 32px;
  font-weight: 500;
  line-height: 0.93;
  background: linear-gradient(109deg, #ececec 52.01%, rgba(236, 236, 236, 0) 135.73%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

@media (min-width: 768px) {
  .faq-title {
    font-size: 40px;
  }
}
</style>
